import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import LoadingComponent from "../components/LoadingComponent";
import TableLoader from "../components/TableLoader";
import ShippingZoneActions from "../components/ShippingZoneActions";
import EditShippingZoneModal from "../components/modals/ShippingZone/EditShippingZoneModal.jsx";
import { useShippingZones } from "../hooks/useShippingZone";
import { useAuth } from "../hooks/useAuth.jsx";
import { useShippingZoneOptions } from "../hooks/useShippingZoneOptions";
import useSocket from "../hooks/useSocket.jsx";
import { useRegions } from "../hooks/useRegions.jsx";

const ShippingZone = () => {
  const navigate = useNavigate();
  const {
    shippingZones,
    loading,
    error,
    pagination,
    filters,
    shippingZonesCount,
    handleChangePage,
    handleChangeRowsPerPage,
    createZone,
    updateZone,
    deleteZone,
    refetchShippingZones,
  } = useShippingZones();
  const { loadingOptions, communeOptions, courierOptions, adminOptions } =
    useShippingZoneOptions();
  const { regions, loadingRegions, fetchRegions } = useRegions();

  const { user } = useAuth();
  const [isAdmin] = useState(() => user.user_type.includes("admin"));
  const [isStaff] = useState(() => user.is_staff);
  const [selectedZone, setSelectedZone] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackAlert, setSnackAlert] = useState({ active: false, message: "" });
  const handleSnack = (active, message) => {
    setSnackAlert({ active, message });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackAlert({ active: false, message: "" });
  };

  useSocket({
    userId: user?.id || "",
    userRole: user?.user_type?.[0] || "unknown_role",
    eventPrefix: "shipping_zone_coverage",
    onMessage: (data) => {
      console.log(
        `📥 WebSocket Shipping Zone Coverage Payload: ${JSON.stringify(data)}`,
      );
      if (handleSnack) handleSnack(true, data.message);
    },
  });

  const handleOpenEditModal = (zone) => {
    setSelectedZone(zone);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedZone(null);
  };

  const handleTablePageChange = (event, newPage) => {
    handleChangePage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    handleChangeRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleUpdateShippingZone = async (data) => {
    try {
      await updateZone(data.id, data);
      alert("Zona de despacho actualizada");
      handleCloseModal();
    } catch (error) {
      console.error(`Error al realizar el request: ${error}`);
    } finally {
      handleCloseModal();
    }
  };

  const handleDelete = async (zoneId) => {
    if (window.confirm("Estas seguro que quieres eliminar esta zona?")) {
      await deleteZone(zoneId);
    }
  };

  return (
    <>
      {loading || loadingOptions ? <LoadingComponent /> : null}
      {error ? (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      ) : null}
      <Grid item xs={12} className="mg-t-20">
        <h2>Mis Zonas de Despacho</h2>
      </Grid>

      <ShippingZoneActions
        loading={loading || loadingOptions}
        isAdmin={isAdmin}
        isStaff={isStaff}
        error={error}
        createZone={createZone}
        communeOptions={communeOptions}
        courierOptions={courierOptions}
        adminOptions={adminOptions}
        regions={regions}
        loadingRegions={loadingRegions}
        fetchRegions={fetchRegions}
      />

      <Grid container>
        <Grid container className="custom-table-container">
          <Grid item xs={12} className="flex-center mg-t-0">
            <>
              <TableContainer>
                {loading || loadingOptions ? (
                  <TableLoader />
                ) : (
                  <>
                    <Table
                      aria-label="tabla de Zonas de despacho"
                      className="fixed-table"
                    >
                      <TableHead sx={{ backgroundColor: "#fff" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Nombre
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Courier
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Comunas
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Precio
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Admin
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }} align="right">
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shippingZones.map((zone) => (
                          <TableRow
                            key={zone.id}
                            hover
                            sx={{
                              "& > *": { borderBottom: "unset" },
                              backgroundColor: "#fff",
                            }}
                          >
                            <TableCell component="th" scope="row">
                              <IconButton
                                onClick={() =>
                                  navigate(
                                    `/home/my-shipping-zones/${zone.id}/detail`,
                                  )
                                }
                              >
                                <i className="material-icons">add</i>
                              </IconButton>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {zone.name}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {zone.courier}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {zone.communes || 0}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              ${zone.price || 0}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {zone.admin_email || "N/A"}
                            </TableCell>
                            <TableCell component="th" scope="row" align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditModal(zone)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(zone.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

                {!loading && shippingZones.length === 0 && (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No se encontraron zonas de despacho.
                    </Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[20, 40, 60, 80, 100]}
                  component="div"
                  count={shippingZonesCount}
                  rowsPerPage={pagination.rowsPerPage}
                  page={pagination.page - 1}
                  onPageChange={handleTablePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </TableContainer>
              <EditShippingZoneModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleUpdateShippingZone}
                initialData={selectedZone}
                communeOptions={communeOptions}
                courierOptions={courierOptions}
                adminOptions={adminOptions}
                regions={regions}
                loadingRegions={loadingRegions}
                fetchRegions={fetchRegions}
              />
            </>
          </Grid>
        </Grid>
      </Grid>
      <Snackbar
        open={snackAlert.active}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        message={snackAlert.message}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSnackClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
};

export default memo(ShippingZone);
